import { useState } from "react";
import { Phone, MessageCircle, ChevronRight } from "lucide-react";
import { allMachines, contactInfo } from "@/data/machines";
import PhotoPlaceholder from "@/components/site/PhotoPlaceholder";
import MachineDialog from "@/components/site/MachineDialog";
import Reveal, { Stagger, RevealItem } from "@/components/site/Reveal";

function MachineCard({ m, onOpen }) {
  const waLink = `https://wa.me/${contactInfo.whatsapp}?text=${encodeURIComponent(
    `Hola, me interesa alquilar el equipo: ${m.name}. ¿Disponibilidad?`
  )}`;
  const cover = m.image || null;

  return (
    <article
      data-testid={`machine-card-${m.id}`}
      className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-[0_24px_60px_-20px_rgba(2,32,71,0.28)] hover:-translate-y-1.5 hover:border-sky-300 transition-all duration-300 flex flex-col cursor-pointer"
      onClick={() => onOpen(m)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen(m)}
    >
      <div className="relative h-56 overflow-hidden bg-white flex items-center justify-center p-3">
        {cover ? (
          <img
            src={cover}
            alt={`${m.name} en alquiler — Maquinaria Santacruz, Santa Olalla (Toledo)`}
            className="w-full h-full object-contain transition-transform duration-[900ms] ease-out group-hover:scale-[1.05]"
            loading="lazy"
          />
        ) : (
          <PhotoPlaceholder iconName={m.iconName} />
        )}
      </div>

      <div className="p-5 flex flex-col gap-4 flex-1">
        <div>
          <h3 className="font-heading text-lg font-bold tracking-tight text-slate-950">
            {m.name}
          </h3>
          <p className="mt-1.5 text-sm text-slate-600 leading-relaxed line-clamp-3">
            {m.description}
          </p>
          <span
            data-testid={`btn-details-${m.id}`}
            className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-sky-600 group-hover:text-sky-700 underline-offset-4 group-hover:underline"
          >
            Ver ficha completa
            <ChevronRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
          </span>
        </div>

        <div className="flex items-end justify-between mt-auto">
          <a
            href={`tel:${contactInfo.phoneRaw}`}
            data-testid={`phone-${m.id}`}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-slate-800 hover:text-sky-600 transition-colors"
          >
            <Phone className="w-3.5 h-3.5" />
            {contactInfo.phone}
          </a>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <a
            href={`tel:${contactInfo.phoneRaw}`}
            data-testid={`btn-alquilar-${m.id}`}
            onClick={(e) => e.stopPropagation()}
            className="shine col-span-2 inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-semibold rounded-xl px-4 py-2.5 text-sm transition-all duration-300 hover:shadow-md hover:shadow-sky-500/30"
          >
            <Phone className="w-4 h-4" />
            Alquilar
          </a>
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`btn-whatsapp-${m.id}`}
            onClick={(e) => e.stopPropagation()}
            aria-label={`WhatsApp ${m.name}`}
            className="inline-flex items-center justify-center bg-[#25D366] hover:bg-[#1faa55] text-white rounded-xl px-4 py-2.5 transition-all duration-300 hover:shadow-md hover:shadow-emerald-500/30 hover:scale-105"
          >
            <MessageCircle className="w-5 h-5" />
          </a>
        </div>
      </div>
    </article>
  );
}

export default function Catalog() {
  const [activeModel, setActiveModel] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const machines = allMachines.filter((m) => !m.hidden);

  const handleOpen = (m) => {
    setActiveModel({ ...m, longDescription: m.description });
    setDialogOpen(true);
  };

  return (
    <section
      id="catalogo"
      data-testid="catalog-section"
      className="py-16 sm:py-20 md:py-28 bg-slate-50 relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -top-40 right-0 w-[28rem] h-[28rem] bg-sky-100/60 blur-3xl rounded-full pointer-events-none"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <Reveal className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-sky-600">
              <span className="w-6 h-px bg-sky-500" /> Catálogo
            </span>
            <h2 className="font-heading mt-4 text-3xl md:text-5xl font-bold tracking-tight text-slate-950 leading-[1.05]">
              Catálogo de Maquinaria
            </h2>
            <p className="mt-5 text-slate-600 text-base md:text-lg leading-relaxed">
              Pulsa una máquina para ver su ficha completa. También puedes llamarnos al{" "}
              <a href={`tel:${contactInfo.phoneRaw}`} className="font-semibold text-sky-600 hover:text-sky-700">
                {contactInfo.phone}
              </a>{" "}
              o escribirnos por WhatsApp.
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <a
              href={`tel:${contactInfo.phoneRaw}`}
              data-testid="catalog-cta-call"
              className="group inline-flex items-center gap-2 bg-slate-950 hover:bg-slate-800 text-white font-semibold rounded-xl px-5 py-3 text-sm transition-all duration-300 hover:-translate-y-0.5 shadow-sm hover:shadow-md"
            >
              <Phone className="w-4 h-4 group-hover:wiggle" /> {contactInfo.phone}
            </a>
          </Reveal>
        </div>

        <Stagger
          data-testid="machines-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6"
        >
          {machines.map((m) => (
            <RevealItem key={m.id}>
              <MachineCard m={m} onOpen={handleOpen} />
            </RevealItem>
          ))}
        </Stagger>
      </div>

      <MachineDialog machine={activeModel} open={dialogOpen} onOpenChange={setDialogOpen} />
    </section>
  );
}
